/**
 * The way back to Easy Entry Mode from a page it opened.
 *
 * Easy Entry opens full Finance pages for what it does not draw itself — the
 * masters under Create / Alter (Party, Bank Account, Department…), My
 * Approvals, the Dashboard, the pages on Go To. Before it goes it leaves a
 * return point here: where it was (screen, menu, period, the report and the
 * line it was on) and a name for it. Easy Entry reads the return point when it
 * opens again and lands exactly there, then clears it.
 *
 * TallyReturnBar shows on every Finance page while a return point is held (it
 * is mounted in Finance2RouteBoundary, so the setup masters — which do not use
 * Finance2Shell — get it too):
 *
 *   ← Back to Easy Entry · Create           Esc / Alt+Q
 *
 * Esc goes back only when nothing else wants it: no dialog or drawer open, the
 * cursor not in a field. Alt+Q goes back from anywhere. The browser's own Back
 * lands on the same place, because the return point is read on the way in.
 *
 * Kept in sessionStorage: one tab's trip, gone with the tab, and stale after a
 * few hours so an old trip does not offer a way back nobody remembers.
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

const KEY = 'f2:tally-return';
const MAX_AGE_MS = 4 * 60 * 60 * 1000;
export const TALLY_ENTRY_PATH = '/app/maud-erp/finance2/tally-entry';

export interface TallyReturn {
  /** Where Easy Entry was, shown on the bar: "Create", "Display More Reports", "Trial Balance". */
  label: string;
  /** The page's own state to restore — Easy Entry reads its fields back. */
  state: Record<string, unknown>;
  at: number;
}

/** Easy Entry leaving for another page: remember where it was. */
export function saveTallyReturn(label: string, state: Record<string, unknown>): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ label, state, at: Date.now() } satisfies TallyReturn));
  } catch { /* storage blocked: the bar just does not show */ }
}

/**
 * The other direction: a full Finance page sending the reader INTO Easy Entry,
 * landing on one of its screens rather than on the Gateway.
 *
 * Easy Entry restores its screen, period and view from the same record it
 * leaves for itself (TallyEntryPage's `was`), so a page that wants to open it on
 * the Balance Sheet only has to say so here and navigate to TALLY_ENTRY_PATH.
 *
 * No label, because the bar is for the trip OUT of Easy Entry and this is the
 * trip in: Easy Entry reads the record on mount and clears it, so the bar never
 * gets a page to show itself on. Keys the caller leaves out keep Easy Entry's
 * own defaults — `was` falls back only on `undefined`, so a date the caller does
 * not have must be omitted rather than sent as null.
 */
export function openTallyAt(state: Record<string, unknown>): void {
  saveTallyReturn('', state);
}

/** The return point, if one is held and fresh. Reading does not clear it. */
export function readTallyReturn(): TallyReturn | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as TallyReturn;
    if (!r || typeof r.at !== 'number' || Date.now() - r.at > MAX_AGE_MS) { clearTallyReturn(); return null; }
    return r;
  } catch { return null; }
}

export function clearTallyReturn(): void {
  try { window.sessionStorage.removeItem(KEY); } catch { /* nothing held */ }
}

const typing = (el: Element | null) => !!el && (
  el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || (el as HTMLElement).isContentEditable
);
const modalOpen = () => !!document.querySelector('.MuiModal-root:not(.MuiModal-hidden), .MuiPopover-root, [role="dialog"]');

/** The "Back to Easy Entry" bar, on any Finance page opened from Easy Entry. */
export const TallyReturnBar: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onEntry = pathname.startsWith(TALLY_ENTRY_PATH);
  const [ret, setRet] = useState<TallyReturn | null>(() => (onEntry ? null : readTallyReturn()));
  // A new page may have been reached by a new trip out of Easy Entry: read again.
  useEffect(() => { setRet(onEntry ? null : readTallyReturn()); }, [pathname, onEntry]);

  const back = () => navigate(TALLY_ENTRY_PATH);

  useEffect(() => {
    if (!ret) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const altQ = e.altKey && !e.ctrlKey && !e.metaKey && (e.key.toLowerCase() === 'q' || e.code === 'KeyQ');
      const esc = e.key === 'Escape' && !e.altKey && !e.ctrlKey && !e.metaKey && !modalOpen() && !typing(document.activeElement);
      if (!altQ && !esc) return;
      e.preventDefault();
      back();
    };
    // Bubble phase, after the page: a page that handles Esc itself (a drawer, a search box) keeps it.
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // back only navigates; it does not change with ret.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ret]);

  if (!ret) return null;
  return (
    <Box
      role="navigation"
      aria-label="Back to Easy Entry Mode"
      sx={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 16, zIndex: 1300, maxWidth: 'calc(100vw - 24px)',
        display: 'flex', alignItems: 'center', gap: 1, pl: 0.5, pr: 1.25, py: 0.5, borderRadius: 2,
        bgcolor: '#1f6274', color: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
      }}
    >
      <Button
        size="small" onClick={back} startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{ color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 12.5, px: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' } }}
      >
        Back to Easy Entry{ret.label ? ` · ${ret.label}` : ''}
      </Button>
      <Typography component="span" sx={{ fontSize: 11, opacity: 0.85, whiteSpace: 'nowrap' }}>
        <b>Esc</b> / <b>Alt+Q</b>
      </Typography>
    </Box>
  );
};
